import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, FormControl, IconButton,
  InputLabel, MenuItem, Paper, Select, Tab, Table, TableBody, TableCell, TableContainer,
  TableHead, TablePagination, TableRow, Tabs, TextField, Tooltip, Typography,
  alpha, useTheme,
} from '@mui/material';
import {
  Add as AddIcon, ArrowBack as BackIcon, Delete as DeleteIcon,
  Edit as EditIcon, Search as SearchIcon,
} from '@mui/icons-material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useVenue } from '../../../../context/VenueContext';
import menuService from '../../../../services/menuService';
import menuItemService from '../../../../services/menuItemService';
import menuSectionService from '../../../../services/menuSectionService';
import type { Menu as MenuDoc, MenuItem as MenuItemDoc, MenuItemKind, MenuSection, MenuStatus, MenuType, MenuVisibility } from '../../../../types/menu';
import MenuSectionsPanel from '../../../../components/venue/MenuSectionsPanel';
import MenuFormatsPanel from '../../../../components/venue/MenuFormatsPanel';
import MenuPreviewPanel from '../../../../components/venue/MenuPreviewPanel';
import MenuPublishPanel from '../../../../components/venue/MenuPublishPanel';
import MenuRecipesPanel from '../../../../components/venue/MenuRecipesPanel';
import { menuTypeLabel } from './menuTypeLabels';
import type { MenuRenderResponse } from '../../../../types/menu';

const STATUS_COLORS: Record<MenuStatus, 'default' | 'primary' | 'success' | 'warning'> = {
  DRAFT: 'default',
  ACTIVE: 'success',
  ARCHIVED: 'warning',
};

/**
 * Menu types where the primary item kind is WINE. Drives the "Add Wine"
 * button label + ?kind=WINE routing, and exposes the Producer/Vintage table
 * columns. Mirrors the WINE_INFERRED_MENU_TYPES list in MenuItemForm dispatcher.
 */
const WINE_MENU_TYPES: MenuType[] = ['FULL', 'BY_THE_GLASS', 'RESERVE', 'EVENT', 'PRIVATE'];
const isWineMenuType = (type: MenuType): boolean => WINE_MENU_TYPES.includes(type);

// Phase 7.C.2b/7.C.3a — non-wine menu types and the item kind they author + the
// add-button label. Mirrors MenuItemForm's MENU_TYPE_TO_ITEM_KIND (incl. the
// SPIRITS→SPIRIT rename). TASTING stays absent (still gated).
const NON_WINE_ADD: Partial<Record<MenuType, { kind: MenuItemKind; label: string }>> = {
  COCKTAIL: { kind: 'COCKTAIL', label: 'Add Cocktail' },
  FOOD: { kind: 'DISH', label: 'Add Dish' },
  BEER: { kind: 'BEER', label: 'Add Beer' },
  SPIRITS: { kind: 'SPIRIT', label: 'Add Spirit' },
  RETAIL: { kind: 'RETAIL', label: 'Add Item' },
  OTHER: { kind: 'OTHER', label: 'Add Item' },
};

// MIXED-menu add-item picker options (TASTING_MENU intentionally excluded).
const MIXED_PICKER_OPTIONS: { kind: MenuItemKind; label: string }[] = [
  { kind: 'WINE', label: 'Add Wine' },
  { kind: 'COCKTAIL', label: 'Add Cocktail' },
  { kind: 'DISH', label: 'Add Dish' },
  { kind: 'BEER', label: 'Add Beer' },
  { kind: 'SPIRIT', label: 'Add Spirit' },
  { kind: 'RETAIL', label: 'Add Item' },
  { kind: 'OTHER', label: 'Add Item' },
];

type TabKey = 'items' | 'sections' | 'formats' | 'preview' | 'share' | 'recipes';

/**
 * Phase 4.J — per-item recipe-link summary derived from the admin render
 * endpoint. Used by the items-table indicator column (Part C) and the
 * embedded recipes panel (Part B). Built once per menu render so we never
 * issue per-row queries.
 */
interface ItemRecipeSummary {
  totalFormats: number;
  recipeLinkedFormats: number;
}

const thStyle = {
  fontWeight: 600,
  fontSize: '0.75rem',
  textTransform: 'uppercase' as const,
};

const MenuDetail: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { menuId } = useParams<{ menuId: string }>();
  const [searchParams] = useSearchParams();
  const { venueId, defaultCurrency } = useVenue();
  const base = `/admin/venues/${venueId}/menus`;

  const [menu, setMenu] = useState<MenuDoc | null>(null);
  const [menuLoading, setMenuLoading] = useState(true);
  const [menuError, setMenuError] = useState('');
  // Initial tab can be deep-linked via ?tab= (e.g. the item form's
  // "Go to Serving sizes" guidance links to ?tab=formats). Defaults to 'items'.
  const [tab, setTab] = useState<TabKey>(() => {
    const requested = searchParams.get('tab');
    const valid: TabKey[] = ['items', 'sections', 'formats', 'preview', 'share', 'recipes'];
    return requested && valid.includes(requested as TabKey) ? (requested as TabKey) : 'items';
  });

  // Items state — only loaded while the Items tab is active (or has been
  // visited at least once). Section name lookup uses the loaded sections.
  const [items, setItems] = useState<MenuItemDoc[]>([]);
  const [itemsLoading, setItemsLoading] = useState(true);
  const [itemsError, setItemsError] = useState('');
  const [itemsPagination, setItemsPagination] = useState({ page: 0, pageSize: 25, totalItems: 0 });
  const [search, setSearch] = useState('');
  const [visibilityFilter, setVisibilityFilter] = useState<MenuVisibility | ''>('');

  // Sections — used to display section name in the items table.
  const [sections, setSections] = useState<MenuSection[]>([]);
  const sectionsById = useMemo(
    () => new Map(sections.map(s => [s._id, s])),
    [sections],
  );

  // MIXED-menu kind picker dialog.
  const [pickKindOpen, setPickKindOpen] = useState(false);

  // Phase 4.J — single render call gives us per-item format presence + per-
  // format consumptionRecipeId in one trip. Powers both the items-table
  // recipe-indicator column (Part C) and the Recipes panel's "linked on this
  // menu" count (Part B). Failure is non-blocking — the rest of MenuDetail
  // works fine without the recipe summaries.
  const [renderData, setRenderData] = useState<MenuRenderResponse | null>(null);

  // ── Load menu ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!menuId) return;
    setMenuLoading(true);
    setMenuError('');
    menuService.getMenu(venueId, menuId)
      .then(setMenu)
      .catch(() => setMenuError('Could not load menu. Please try again.'))
      .finally(() => setMenuLoading(false));
  }, [venueId, menuId]);

  // ── Load sections (lightweight, used for items table lookup) ───────────────
  useEffect(() => {
    if (!menuId) return;
    menuSectionService.getSections(venueId, menuId)
      .then(setSections)
      .catch(() => { /* non-blocking */ });
  }, [venueId, menuId]);

  // ── Phase 4.J: Load render data (for recipe-link summaries) ────────────────
  // One call per menu mount. Fails silently so the rest of the page works
  // when the menu is ARCHIVED (render endpoint refuses archived) or when the
  // operator is offline.
  useEffect(() => {
    if (!menuId) return;
    menuService.renderMenu(venueId, menuId)
      .then(setRenderData)
      .catch(() => { /* non-blocking — recipe summaries simply omit */ });
  }, [venueId, menuId]);

  // Build the per-item recipe summary map from render data.
  const itemRecipeSummaryById = useMemo(() => {
    const map = new Map<string, ItemRecipeSummary>();
    if (!renderData) return map;
    for (const section of renderData.sections) {
      for (const item of section.items) {
        const formats = item.formats ?? [];
        const recipeLinkedFormats = formats.filter(f => !!f.consumptionRecipeId).length;
        map.set(item.id, { totalFormats: formats.length, recipeLinkedFormats });
      }
    }
    return map;
  }, [renderData]);

  // Build the per-recipe link-count map (recipeId → number of format-rows
  // on this menu pointing at it). Powers the "Linked on this menu" column
  // in MenuRecipesPanel.
  const recipeLinkCountById = useMemo(() => {
    const map = new Map<string, number>();
    if (!renderData) return map;
    for (const section of renderData.sections) {
      for (const item of section.items) {
        for (const fmt of item.formats ?? []) {
          if (fmt.consumptionRecipeId) {
            map.set(fmt.consumptionRecipeId, (map.get(fmt.consumptionRecipeId) ?? 0) + 1);
          }
        }
      }
    }
    return map;
  }, [renderData]);

  // Phase 4.M — count of distinct COCKTAIL items on this menu that have at
  // least one serving-size row carrying a recipe link. Feeds the operational
  // summary at the top of MenuRecipesPanel.
  const cocktailItemsUsingRecipes = useMemo(() => {
    if (!renderData) return 0;
    let count = 0;
    for (const section of renderData.sections) {
      for (const item of section.items) {
        if (item.kind !== 'COCKTAIL') continue;
        if ((item.formats ?? []).some(f => !!f.consumptionRecipeId)) count += 1;
      }
    }
    return count;
  }, [renderData]);

  // Phase 4.N — menu-wide setup summary. Derived ONLY from already-loaded
  // data: `renderData` (full menu render, Phase 4.J), `sections` (active
  // section list, loaded for the items table), and the `recipeLinkCountById`
  // map (Phase 4.J derivation). Null while render data is loading or when
  // the render endpoint refused the menu (e.g., ARCHIVED — see Phase 4.B).
  const menuSummary = useMemo(() => {
    if (!renderData) return null;
    const allItems = renderData.sections.flatMap(s => s.items ?? []);
    const totalItems = allItems.length;
    const hiddenItems = allItems.filter(i => i.visibility === 'HIDDEN').length;
    const visibleItems = totalItems - hiddenItems;
    // "Serving sizes" = distinct formatIds across all items on this menu.
    // This is the closest we can come to "serving sizes configured" without
    // a separate fetch of the MenuFormat catalog. Matches what the operator
    // sees as active serving sizes on this menu.
    const servingSizeIds = new Set<string>();
    for (const item of allItems) {
      for (const fmt of item.formats ?? []) servingSizeIds.add(fmt.formatId);
    }
    return {
      totalItems,
      visibleItems,
      hiddenItems,
      servingSizes: servingSizeIds.size,
      recipesLinked: recipeLinkCountById.size,
      sectionsCount: sections.length,
    };
  }, [renderData, recipeLinkCountById, sections]);

  // Phase 4.N — lightweight setup warnings. Informational only, never
  // blocking. Each string is rendered as a bullet in the Alert below the
  // summary strip; the Alert hides entirely when the list is empty.
  // Derived from the same already-loaded data as the summary.
  const menuWarnings = useMemo<string[]>(() => {
    if (!renderData || !menu) return [];
    const warnings: string[] = [];
    const allItems = renderData.sections.flatMap(s => s.items ?? []);
    const cocktails = allItems.filter(i => i.kind === 'COCKTAIL');
    const wines = allItems.filter(i => i.kind === 'WINE');

    const cocktailsMissingSizes = cocktails.filter(c => (c.formats ?? []).length === 0).length;
    if (cocktailsMissingSizes > 0) {
      warnings.push(
        `${cocktailsMissingSizes} cocktail item${cocktailsMissingSizes === 1 ? ' has' : 's have'} no serving sizes configured.`,
      );
    }
    const cocktailsMissingRecipes = cocktails.filter(c => {
      const fmts = c.formats ?? [];
      return fmts.length > 0 && fmts.every(f => !f.consumptionRecipeId);
    }).length;
    if (cocktailsMissingRecipes > 0) {
      warnings.push(
        `${cocktailsMissingRecipes} cocktail item${cocktailsMissingRecipes === 1 ? '' : 's'} ` +
        `with serving sizes ${cocktailsMissingRecipes === 1 ? 'has' : 'have'} no recipe linked.`,
      );
    }
    const winesMissingInventory = wines.filter(w => !w.inventoryItemId).length;
    if (winesMissingInventory > 0) {
      warnings.push(
        `${winesMissingInventory} wine item${winesMissingInventory === 1 ? ' is' : 's are'} missing an inventory link.`,
      );
    }
    if (sections.length === 0) {
      warnings.push('This menu has no sections yet.');
    }
    if (allItems.length > 0 && allItems.every(i => i.visibility === 'HIDDEN')) {
      warnings.push('This menu has no visible items yet.');
    }
    // Menu-type mismatch flags. Wine-list menu types are the same set used
    // elsewhere in this file (see WINE_MENU_TYPES at the top).
    if (menu.type === 'COCKTAIL' && wines.length > 0) {
      warnings.push(
        `This is a cocktail menu but contains ${wines.length} wine item${wines.length === 1 ? '' : 's'}.`,
      );
    } else if (WINE_MENU_TYPES.includes(menu.type) && cocktails.length > 0) {
      warnings.push(
        `This menu's type is a wine list but contains ${cocktails.length} cocktail item${cocktails.length === 1 ? '' : 's'}.`,
      );
    }
    return warnings;
  }, [renderData, menu, sections]);

  // ── Load items (paginated, server-side) ────────────────────────────────────
  const fetchItems = useCallback(
    async (page: number, pageSize: number, vis: MenuVisibility | '') => {
      if (!menuId) return;
      setItemsLoading(true);
      setItemsError('');
      try {
        const result = await menuItemService.getItems(
          venueId,
          menuId,
          page + 1,
          pageSize,
          vis ? { visibility: vis } : undefined,
        );
        setItems(result.data);
        setItemsPagination(prev => ({ ...prev, page, pageSize, totalItems: result.pagination.totalItems }));
      } catch {
        setItemsError('Could not load items. Please try again.');
      } finally {
        setItemsLoading(false);
      }
    },
    [venueId, menuId],
  );

  useEffect(() => {
    fetchItems(itemsPagination.page, itemsPagination.pageSize, visibilityFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsPagination.page, itemsPagination.pageSize, visibilityFilter, menuId]);

  // Client-side search filter (display name + subtitle + wine producer when present).
  const filteredItems = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.trim().toLowerCase();
    return items.filter(item => {
      const name = item.displaySnapshot?.name?.toLowerCase() ?? '';
      const sub = item.displaySnapshot?.subtitle?.toLowerCase() ?? '';
      const producer = item.wineDetails?.wineDisplay?.producerName?.toLowerCase() ?? '';
      return name.includes(q) || sub.includes(q) || (producer !== '' && producer.includes(q));
    });
  }, [items, search]);

  const handleDeleteItem = async (e: React.MouseEvent, item: MenuItemDoc) => {
    e.stopPropagation();
    const itemName = item.displaySnapshot?.name ?? 'this item';
    if (!window.confirm(`Delete "${itemName}"? This cannot be undone.`)) return;
    try {
      await menuItemService.deleteItem(venueId, item.menuId, item._id);
      fetchItems(itemsPagination.page, itemsPagination.pageSize, visibilityFilter);
    } catch {
      setItemsError(`Could not delete "${itemName}". Please try again.`);
    }
  };

  const formatItemPrice = (item: MenuItemDoc): string => {
    if (item.basePrice == null) return '—';
    const code = item.currency ?? defaultCurrency ?? '';
    return code ? `${code} ${item.basePrice.toFixed(2)}` : item.basePrice.toFixed(2);
  };

  // ── Add-item UX (kind-aware) ───────────────────────────────────────────────
  // For wine menu types we deep-link with ?kind=WINE so the dispatcher skips
  // the picker. For MIXED we open a small dialog asking Wine vs Cocktail.
  // For other types (FOOD/BEER/SPIRITS/TASTING/RETAIL/OTHER) Phase 4.B can't
  // add items yet — the button is disabled with a tooltip.
  const addButtonLabel = (() => {
    if (!menu) return 'Add Item';
    if (isWineMenuType(menu.type)) return 'Add Wine';
    return NON_WINE_ADD[menu.type]?.label ?? 'Add Item';
  })();
  const addButtonDisabledBecauseMenu = !menu || menu.status === 'ARCHIVED';
  // Authorable = wine types, MIXED, or a kind in NON_WINE_ADD. Anything else
  // (currently only TASTING) stays gated.
  const addButtonDisabledBecauseUnsupportedKind = !!menu
    && menu.type !== 'MIXED'
    && !isWineMenuType(menu.type)
    && !NON_WINE_ADD[menu.type];
  const addButtonDisabled = addButtonDisabledBecauseMenu || addButtonDisabledBecauseUnsupportedKind;
  const addButtonTooltip = !addButtonDisabledBecauseUnsupportedKind
    ? ''
    : menu?.type === 'TASTING'
      ? 'Tasting menu item authoring is coming soon. This menu type may need course and pairing structure, so it is not enabled yet.'
      : `Adding ${menu?.type.replace(/_/g, ' ') ?? ''} items is not supported yet.`;

  // Producer/Vintage columns: shown on wine-typed menus and on MIXED so
  // they're visible alongside wine items. Other types skip them to keep the
  // table narrow.
  const showWineColumns = !!menu && (isWineMenuType(menu.type) || menu.type === 'MIXED');

  const handleAddClick = () => {
    if (!menu) return;
    if (isWineMenuType(menu.type)) {
      navigate(`${base}/${menu._id}/add?kind=WINE`);
      return;
    }
    const nonWine = NON_WINE_ADD[menu.type];
    if (nonWine) {
      navigate(`${base}/${menu._id}/add?kind=${nonWine.kind}`);
      return;
    }
    if (menu.type === 'MIXED') {
      setPickKindOpen(true);
      return;
    }
    // Other types (TASTING) — button is disabled, nothing to do.
  };

  // ── Early returns ──────────────────────────────────────────────────────────
  if (menuLoading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (menuError || !menu) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ borderRadius: 1, mb: 2 }}>
          {menuError || 'Menu not found.'}
        </Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate(base)} sx={{ textTransform: 'none' }}>
          Back to menus
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Header card */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        <Box sx={{ px: 3, py: 2.5, display: 'flex', alignItems: 'center', gap: 2, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}` }}>
          <Button startIcon={<BackIcon />} onClick={() => navigate(base)} sx={{ textTransform: 'none', color: theme.palette.text.secondary }}>
            Menus
          </Button>
          <Divider orientation="vertical" flexItem />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Typography variant="h6" fontWeight={600} noWrap>{menu.name}</Typography>
              <Chip label={menuTypeLabel(menu.type)} size="small" sx={{ fontSize: '0.7rem', height: 22 }} />
              <Chip label={menu.status} size="small" color={STATUS_COLORS[menu.status]} sx={{ fontSize: '0.7rem', height: 22 }} />
            </Box>
            {menu.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {menu.description}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<EditIcon />}
              onClick={() => navigate(`${base}/edit/${menu._id}`)}
              sx={{ textTransform: 'none', fontWeight: 600 }}
              disabled={menu.status === 'ARCHIVED'}
            >
              Edit
            </Button>
            <Tooltip title={addButtonTooltip} placement="bottom" disableHoverListener={!addButtonTooltip}>
              <span>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleAddClick}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                  disabled={addButtonDisabled}
                >
                  {addButtonLabel}
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Box>
      </Paper>

      {/* Phase 4.N — operational summary strip + setup warnings.
          Lives between the header and the tabs so the operator gets a
          one-glance readout of menu setup state without opening any tab.
          Both blocks are derived from already-loaded data; nothing here
          triggers a backend call. The strip is hidden while renderData is
          still loading or unavailable (e.g., for ARCHIVED menus where the
          render endpoint refuses) so we never display misleading zeros. */}
      {menuSummary && (
        <Paper sx={{ borderRadius: 2, p: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              rowGap: 1.5,
              columnGap: 3,
            }}
          >
            {[
              { label: 'Items',         value: menuSummary.totalItems },
              { label: 'Visible',       value: menuSummary.visibleItems },
              { label: 'Hidden',        value: menuSummary.hiddenItems },
              { label: 'Serving sizes', value: menuSummary.servingSizes },
              { label: 'Recipes linked', value: menuSummary.recipesLinked },
              { label: 'Sections',      value: menuSummary.sectionsCount },
            ].map(stat => (
              <Box key={stat.label} sx={{ minWidth: 96 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}
                >
                  {stat.label}
                </Typography>
                <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.95rem' }}>
                  {stat.value}
                </Typography>
              </Box>
            ))}
          </Box>
          {menuWarnings.length > 0 && (
            <Alert
              severity="info"
              icon={false}
              sx={{
                mt: 2,
                borderRadius: 1,
                py: 0.5,
                '& .MuiAlert-message': { py: 0.5 },
              }}
            >
              <Box component="ul" sx={{ m: 0, pl: 2.25 }}>
                {menuWarnings.map((w, i) => (
                  <Typography
                    component="li"
                    key={i}
                    variant="caption"
                    sx={{ display: 'list-item', lineHeight: 1.6 }}
                  >
                    {w}
                  </Typography>
                ))}
              </Box>
            </Alert>
          )}
        </Paper>
      )}

      {/* Tabs */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        <Tabs
          value={tab}
          onChange={(_e, v) => setTab(v as TabKey)}
          sx={{ borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`, px: 2 }}
        >
          <Tab label="Items" value="items" sx={{ textTransform: 'none', fontWeight: 600 }} />
          <Tab label="Sections" value="sections" sx={{ textTransform: 'none', fontWeight: 600 }} />
          {/* Phase 4.M — tab label is now "Serving sizes" but the tab key
              stays 'formats' so existing deep-links / state references work. */}
          <Tab label="Serving sizes" value="formats" sx={{ textTransform: 'none', fontWeight: 600 }} />
          <Tab label="Preview" value="preview" sx={{ textTransform: 'none', fontWeight: 600 }} />
          <Tab label="Share" value="share" sx={{ textTransform: 'none', fontWeight: 600 }} />
          <Tab label="Recipes" value="recipes" sx={{ textTransform: 'none', fontWeight: 600 }} />
        </Tabs>

        {tab === 'items' && (
          <Box>
            {/* Phase 7.C.3b — Tasting menus are gated: item authoring is not
                enabled yet (may need course/pairing structure). Coming-soon
                banner instead of a broken-looking empty Add. */}
            {menu?.type === 'TASTING' && (
              <Box sx={{ px: 3, pt: 2 }}>
                <Alert severity="info" sx={{ borderRadius: 1 }}>
                  <Typography variant="body2" fontWeight={600} sx={{ mb: 0.25 }}>
                    Tasting menu setup is coming soon.
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    Future support may include courses, pairings and menu-level stock/recipe setup. Use Food, Wine, Cocktail or Mixed menus for current item authoring.
                  </Typography>
                </Alert>
              </Box>
            )}
            {/* Phase 7.C.4a — prerequisite hint: items can't carry priced serving
                options until the menu has serving sizes. Shown only when there
                are none, so it stays quiet once the menu is set up. Suppressed for
                TASTING (gated — authoring isn't available, so the hint is moot). */}
            {menuSummary && menuSummary.servingSizes === 0 && menu?.type !== 'TASTING' && (
              <Box sx={{ px: 3, pt: 2 }}>
                <Alert
                  severity="info"
                  sx={{ borderRadius: 1 }}
                  action={
                    <Button color="inherit" size="small" onClick={() => setTab('formats')} sx={{ textTransform: 'none' }}>
                      Go to Serving sizes
                    </Button>
                  }
                >
                  Add serving sizes before adding item prices and stock setup. Serving sizes define options like Bottle, 150ml glass, Plate or Bowl.
                </Alert>
              </Box>
            )}
            {/* Phase 4.M — subtle hierarchy caption: clarifies what items inherit. */}
            <Box sx={{ px: 3, pt: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Items inherit serving sizes, recipes and operational settings.
              </Typography>
            </Box>
            {/* Filters */}
            <Box sx={{ px: 3, py: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}` }}>
              <TextField
                size="small"
                placeholder="Search name, subtitle, or producer"
                value={search}
                onChange={e => setSearch(e.target.value)}
                sx={{ flex: 1, minWidth: 220, maxWidth: 360 }}
                slotProps={{ input: { startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.disabled' }} /> } }}
              />
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Visibility</InputLabel>
                <Select
                  value={visibilityFilter}
                  label="Visibility"
                  onChange={e => {
                    setVisibilityFilter(e.target.value as MenuVisibility | '');
                    setItemsPagination(p => ({ ...p, page: 0 }));
                  }}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="LIVE">LIVE</MenuItem>
                  <MenuItem value="HIDDEN">HIDDEN</MenuItem>
                </Select>
              </FormControl>
              <Box sx={{ flex: 1 }} />
              <Tooltip title={addButtonTooltip} placement="bottom" disableHoverListener={!addButtonTooltip}>
                <span>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={handleAddClick}
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                    disabled={addButtonDisabled}
                  >
                    {addButtonLabel}
                  </Button>
                </span>
              </Tooltip>
            </Box>

            {itemsError && (
              <Box sx={{ px: 3, pt: 2 }}>
                <Alert severity="error" onClose={() => setItemsError('')} sx={{ borderRadius: 1 }}>{itemsError}</Alert>
              </Box>
            )}

            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table sx={{ minWidth: 720 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...thStyle, color: theme.palette.text.secondary }}>Name</TableCell>
                    {showWineColumns && (
                      <TableCell sx={{ ...thStyle, color: theme.palette.text.secondary }}>Producer</TableCell>
                    )}
                    {showWineColumns && (
                      <TableCell sx={{ ...thStyle, color: theme.palette.text.secondary }}>Vintage</TableCell>
                    )}
                    <TableCell sx={{ ...thStyle, color: theme.palette.text.secondary }}>Subtitle</TableCell>
                    <TableCell sx={{ ...thStyle, color: theme.palette.text.secondary }}>Section</TableCell>
                    <TableCell sx={{ ...thStyle, color: theme.palette.text.secondary }}>Visibility</TableCell>
                    <TableCell sx={{ ...thStyle, color: theme.palette.text.secondary }} align="right">Base Price</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {itemsLoading ? (
                    <TableRow>
                      <TableCell colSpan={showWineColumns ? 8 : 6}>
                        <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
                          <CircularProgress size={28} />
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : filteredItems.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={showWineColumns ? 8 : 6}>
                        <Box sx={{ py: 6, textAlign: 'center', color: theme.palette.text.secondary }}>
                          <Typography variant="body2" fontWeight={500} gutterBottom>
                            {search || visibilityFilter ? 'No items match the current filters.' : 'No items on this menu yet.'}
                          </Typography>
                          {!search && !visibilityFilter && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 460, mx: 'auto', mb: 1 }}>
                              {/* Phase 4.N — kind-aware operational guidance.
                                  Reuses isWineMenuType() defined at the top of
                                  this file. MIXED/anything else falls back to
                                  the generic copy. */}
                              {isWineMenuType(menu.type)
                                ? 'Add wines to begin building the list.'
                                : menu.type === 'COCKTAIL'
                                  ? 'Add cocktails and serving sizes to begin building the menu.'
                                  : menu.type === 'FOOD'
                                    ? 'Add dishes to begin building the menu.'
                                    : 'Add items to begin building the menu.'}
                            </Typography>
                          )}
                          {!search && !visibilityFilter && !addButtonDisabled && (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<AddIcon />}
                              onClick={handleAddClick}
                              sx={{ mt: 1, textTransform: 'none' }}
                            >
                              {addButtonLabel}
                            </Button>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredItems.map(item => {
                      const sectionName = item.sectionId
                        ? sectionsById.get(item.sectionId)?.name ?? '—'
                        : 'Unsectioned';
                      // Phase 4.J — recipe-link summary, derived from the
                      // render endpoint already loaded for this menu. Only
                      // meaningful for COCKTAIL items (wine items don't carry
                      // recipes in this phase). When the render endpoint
                      // hasn't responded yet (or failed), the summary is
                      // simply omitted — never inferred as "no recipe".
                      const recipeSummary = itemRecipeSummaryById.get(item._id);
                      const showRecipeChip = item.kind === 'COCKTAIL' && !!recipeSummary;
                      const recipeChipLabel =
                        recipeSummary && recipeSummary.totalFormats === 0
                          ? 'No serving sizes'
                          : recipeSummary && recipeSummary.recipeLinkedFormats === 0
                            ? 'No recipe'
                            : recipeSummary
                              ? `${recipeSummary.recipeLinkedFormats}/${recipeSummary.totalFormats} serving size${recipeSummary.totalFormats === 1 ? '' : 's'} linked`
                              : '';
                      const recipeChipColor =
                        recipeSummary && recipeSummary.recipeLinkedFormats > 0 ? 'primary' : 'default';
                      // Phase 4.L — operational summary line below the name.
                      // Phase 4.M — extended to include inventory-link state
                      // alongside the serving/recipe info. Still derived
                      // entirely from already-loaded item + render data;
                      // no extra backend calls and no stock calculations.
                      //   COCKTAIL → "<n> serving sizes · <m> recipes linked · inventory linked · visible"
                      //              parts collapse when not applicable
                      //   WINE     → "<size> ml · inventory linked · visible"
                      //              or "no inventory link" when neither set
                      const visibilityWord = item.visibility === 'HIDDEN' ? 'hidden' : 'visible';
                      const hasInventoryLink = !!item.inventoryItemId;
                      const inventoryPart = hasInventoryLink ? 'inventory linked' : 'no inventory link';

                      // Phase 4.N — quick-status chips. Capped at 2 per row.
                      // Operational state takes priority; Hidden is the
                      // secondary chip so the operator sees what's wrong
                      // before why it's not on display. All derived from
                      // already-loaded data (recipeSummary + item flags).
                      const statusChips: Array<{ label: string; color: 'success' | 'warning' | 'default' }> = [];
                      if (item.kind === 'COCKTAIL' && recipeSummary) {
                        if (recipeSummary.totalFormats === 0) {
                          statusChips.push({ label: 'Missing serving size', color: 'warning' });
                        } else if (recipeSummary.recipeLinkedFormats === 0) {
                          statusChips.push({ label: 'Missing recipe', color: 'warning' });
                        } else if (item.visibility !== 'HIDDEN') {
                          statusChips.push({ label: 'Ready', color: 'success' });
                        }
                      } else if (item.kind === 'WINE') {
                        if (!hasInventoryLink) {
                          statusChips.push({ label: 'Missing inventory', color: 'warning' });
                        } else if (item.visibility !== 'HIDDEN') {
                          statusChips.push({ label: 'Inventory linked', color: 'success' });
                        }
                      }
                      if (item.visibility === 'HIDDEN') {
                        statusChips.push({ label: 'Hidden', color: 'default' });
                      }
                      const visibleStatusChips = statusChips.slice(0, 2);
                      let operationalSummary = '';
                      if (item.kind === 'COCKTAIL' && recipeSummary) {
                        const totalFmts = recipeSummary.totalFormats;
                        const linked = recipeSummary.recipeLinkedFormats;
                        const sizesPart = totalFmts === 0
                          ? 'No serving sizes'
                          : `${totalFmts} serving size${totalFmts === 1 ? '' : 's'}`;
                        const recipePart = totalFmts === 0
                          ? null
                          : linked === 0
                            ? 'No recipe linked'
                            : `${linked} recipe${linked === 1 ? '' : 's'} linked`;
                        operationalSummary = [sizesPart, recipePart, inventoryPart, visibilityWord]
                          .filter((s): s is string => !!s).join(' · ');
                      } else if (item.kind === 'WINE') {
                        const bottleSize = item.wineDetails?.bottleSize;
                        // For wine, surface bottle size and inventory link as
                        // separate parts when both are present (per Phase 4.M
                        // spec). Examples:
                        //   "750 ml · inventory linked · visible"
                        //   "Bottle linked · inventory linked · hidden"  (no recorded size)
                        //   "no inventory link · visible"                (neither)
                        const sizePart = bottleSize ? `${bottleSize} ml` : null;
                        const linkPart = bottleSize == null && hasInventoryLink
                          ? 'Bottle linked'
                          : inventoryPart;
                        operationalSummary = [sizePart, linkPart, visibilityWord]
                          .filter((s): s is string => !!s).join(' · ');
                      }
                      return (
                        <TableRow
                          key={item._id}
                          hover
                          onClick={() => navigate(`${base}/${menu._id}/edit/${item._id}`)}
                          sx={{ cursor: 'pointer', opacity: item.visibility === 'HIDDEN' ? 0.6 : 1 }}
                        >
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              {item.kind === 'WINE' && (
                                <Chip label="Wine" size="small" sx={{ fontSize: '0.62rem', height: 18, fontFamily: 'monospace' }} />
                              )}
                              <Typography variant="body2" fontWeight={600}>
                                {item.displaySnapshot?.name ?? '(unnamed)'}
                              </Typography>
                              {showRecipeChip && (
                                <Tooltip title="Recipes attach to each serving size. This chip summarises how many of the item's serving sizes carry a recipe link.">
                                  <Chip
                                    label={recipeChipLabel}
                                    size="small"
                                    variant={recipeChipColor === 'primary' ? 'filled' : 'outlined'}
                                    color={recipeChipColor}
                                    sx={{ fontSize: '0.62rem', height: 18 }}
                                  />
                                </Tooltip>
                              )}
                              {/* Phase 4.N — quick-status chips. Max 2 per
                                  row, picked operationally first then Hidden. */}
                              {visibleStatusChips.map(chip => (
                                <Chip
                                  key={chip.label}
                                  label={chip.label}
                                  size="small"
                                  color={chip.color === 'default' ? 'default' : chip.color}
                                  variant={chip.color === 'default' ? 'outlined' : 'filled'}
                                  sx={{ fontSize: '0.62rem', height: 18, fontWeight: 500 }}
                                />
                              ))}
                            </Box>
                            {operationalSummary && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block', mt: 0.25, fontSize: '0.7rem' }}
                              >
                                {operationalSummary}
                              </Typography>
                            )}
                          </TableCell>
                          {showWineColumns && (
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {item.wineDetails?.wineDisplay?.producerName ?? '—'}
                              </Typography>
                            </TableCell>
                          )}
                          {showWineColumns && (
                            <TableCell>
                              <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                                {item.wineDetails?.wineDisplay?.displayVintage ?? '—'}
                              </Typography>
                            </TableCell>
                          )}
                          <TableCell>
                            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {item.displaySnapshot?.subtitle || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {sectionName}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={item.visibility}
                              size="small"
                              color={item.visibility === 'LIVE' ? 'success' : 'default'}
                              sx={{ fontSize: '0.7rem', height: 20 }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                              {formatItemPrice(item)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right" onClick={e => e.stopPropagation()}>
                            <Tooltip title="Edit item">
                              <IconButton size="small" onClick={() => navigate(`${base}/${menu._id}/edit/${item._id}`)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete item">
                              <IconButton size="small" color="error" onClick={e => handleDeleteItem(e, item)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={itemsPagination.totalItems}
              page={itemsPagination.page}
              rowsPerPage={itemsPagination.pageSize}
              onPageChange={(_e, newPage) => setItemsPagination(prev => ({ ...prev, page: newPage }))}
              onRowsPerPageChange={e => setItemsPagination(prev => ({ ...prev, pageSize: parseInt(e.target.value, 10), page: 0 }))}
              rowsPerPageOptions={[10, 25, 50]}
            />
          </Box>
        )}

        {/* Phase 4.M — each tab gets a small caption that anchors the
            operator in the Menu → Sections → Items → Serving sizes → Recipes
            hierarchy. Kept to a single Typography per tab so nothing
            visually competes with the panel below. */}
        {tab === 'sections' && (
          <Box>
            <Box sx={{ px: 3, pt: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Sections organize how items appear to guests.
              </Typography>
            </Box>
            <MenuSectionsPanel venueId={venueId} menuId={menu._id} />
          </Box>
        )}

        {tab === 'formats' && (
          <Box>
            <Box sx={{ px: 3, pt: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Serving sizes can be reused across multiple items on this menu.
              </Typography>
            </Box>
            <MenuFormatsPanel venueId={venueId} menuId={menu._id} />
          </Box>
        )}

        {tab === 'preview' && (
          <MenuPreviewPanel venueId={venueId} menuId={menu._id} />
        )}

        {tab === 'share' && (
          <Box sx={{ p: 3 }}>
            <MenuPublishPanel venueId={venueId} menuId={menu._id} menuStatus={menu.status} />
          </Box>
        )}

        {/* Phase 4.J — Recipes tab: embedded recipe surface. Linking still
            lives in each cocktail item's per-serving-size pricing rows; this
            tab is where venue-wide recipes are authored / edited / glanced
            at, with a "linked on this menu" count derived from the render
            endpoint.
            Phase 4.M — caption anchors the hierarchy: recipes attach to
            cocktail serving sizes. */}
        {tab === 'recipes' && (
          <Box>
            <Box sx={{ px: 3, pt: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Recipes attach to cocktail serving sizes.
              </Typography>
            </Box>
            <MenuRecipesPanel
              venueId={venueId}
              linksByRecipeId={recipeLinkCountById}
              cocktailItemsUsingRecipes={cocktailItemsUsingRecipes}
            />
          </Box>
        )}
      </Paper>

      {/* MIXED-menu kind picker dialog — only used when menu.type === 'MIXED'. */}
      <Dialog open={pickKindOpen} onClose={() => setPickKindOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>What do you want to add?</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            This is a mixed menu — pick the item kind.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mt: 1 }}>
            {MIXED_PICKER_OPTIONS.map(opt => (
              <Button
                key={opt.kind}
                variant="contained"
                onClick={() => {
                  setPickKindOpen(false);
                  navigate(`${base}/${menu._id}/add?kind=${opt.kind}`);
                }}
                sx={{ textTransform: 'none', fontWeight: 600, minWidth: 130, flex: '1 1 130px' }}
              >
                {opt.label}
              </Button>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPickKindOpen(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MenuDetail;
